import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import {
  Loader2, AlertTriangle, Save, X, Download, Building2, CalendarRange, RotateCcw,
  ZoomIn, ZoomOut, Maximize2, MoveHorizontal, MoveVertical, Shield, Check,
} from 'lucide-react'
import { api } from '../lib/api.js'
import { useMe, isAdmin } from '../lib/me.js'
import { useConfirm } from './ConfirmProvider.jsx'
import { SURVEY_REGISTRATION } from '../lib/imageTemplates.js'

/**
 * PrImageEditor — Tooth Taxi promo flyer composer.
 *
 *   Accepts a `template` descriptor (see lib/imageTemplates.js) plus a
 *   `saveImage(dataUri)` callback. The same editor handles the survey
 *   registration banner, the 1:1 PR Main, and the A4 PR Main — each just
 *   plugs in a different base image, slot defaults, and save target.
 *
 *   Drag the orange rectangle on the preview to move; drag the corner grip
 *   to resize:
 *     - Text  slots → corner drag changes font size
 *     - QR    slot  → corner drag changes the square side length
 *     - Logo  slot  → corner drag scales width & height independently
 *                     (drag diagonally for proportional, or along one axis
 *                     for that dimension only)
 *
 *   Layout positions persist to localStorage scoped by template key so the
 *   three editors don't share state.
 */
const FONT_STACK = '"IBM Plex Sans Thai","Noto Sans Thai","Sarabun","Helvetica Neue",system-ui,sans-serif'
const TEXT_COLOR = '#222222'

const ZOOM_MIN = 0.1
const ZOOM_MAX = 2.0
const ZOOM_STEP = 1.25

export default function PrImageEditor({
  // Required
  opId, companyId, companyName, startDate, endDate,
  saveImage,                       // (dataUri) => Promise<void>
  // Optional
  template = SURVEY_REGISTRATION,  // see lib/imageTemplates.js
  registrationLink,                // only needed for templates with hasQR
  onClose, onSaved,
}) {
  const canvasRef = useRef(null)
  const wrapRef   = useRef(null)
  const stageRef  = useRef(null)

  const lsKey = `tt-pr-image-coords-${template.key}-v2`

  const me      = useMe()
  const admin   = isAdmin(me)
  const confirm = useConfirm()

  const [zoom, setZoom] = useState(null)
  const [company,  setCompany]  = useState(companyName || '')
  const [dateText, setDateText] = useState(defaultDateText(startDate, endDate))

  // Server-tuned defaults (admin-managed, falls back to in-code defaults).
  // Layered: in-code template.defaults < server image_template_layout < localStorage.
  const [serverDefaults, setServerDefaults] = useState(null)   // { slots, updated_at, updated_by_code } | null
  const [slots, setSlots] = useState(() => {
    let saved = null
    try { saved = JSON.parse(localStorage.getItem(lsKey) || 'null') } catch {}
    return { ...structuredClone(template.defaults), ...(saved && typeof saved === 'object' ? saved : {}) }
  })

  // Fetch server defaults on mount. If the user has NEVER tuned this template
  // on this device (no localStorage entry) we apply server defaults immediately
  // so first-time visitors get the admin-tuned layout. Otherwise we leave the
  // user's personal layout alone and only surface server defaults via the
  // "คืนค่าเริ่มต้น" button.
  useEffect(() => {
    let cancelled = false
    api.imageTemplates.list().then((all) => {
      if (cancelled) return
      const entry = all?.[template.key]
      if (entry?.slots && typeof entry.slots === 'object') {
        setServerDefaults(entry)
        const hasLocal = (() => { try { return !!localStorage.getItem(lsKey) } catch { return false } })()
        if (!hasLocal) {
          setSlots({ ...structuredClone(template.defaults), ...structuredClone(entry.slots) })
        }
      }
    }).catch(() => {})
    return () => { cancelled = true }
  }, [template.key, lsKey])

  // Admin "save as default" state
  const [savingDefault, setSavingDefault] = useState(false)
  const [savedDefault,  setSavedDefault]  = useState(false)
  async function saveAsDefault() {
    if (!admin || savingDefault) return
    setSavingDefault(true); setSavedDefault(false)
    try {
      await api.imageTemplates.saveOne(template.key, slots)
      setServerDefaults({ slots: structuredClone(slots), updated_at: Math.floor(Date.now() / 1000), updated_by_code: me?.code })
      setSavedDefault(true)
      setTimeout(() => setSavedDefault(false), 2000)
    } catch (e) {
      setSaveErr(extractErr(e))
    } finally {
      setSavingDefault(false)
    }
  }

  const [base, setBase]    = useState(null)
  const [qrImg, setQrImg]  = useState(null)
  const [logoImg, setLogoImg] = useState(null)
  const [loadErr, setLoadErr] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState(null)

  const [, forceTick] = useState(0)
  const bump = () => forceTick((n) => n + 1)

  // Load base image
  useEffect(() => {
    setBase(null); setLoadErr(null)
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload  = () => setBase(img)
    img.onerror = () => setLoadErr('โหลดภาพพื้นไม่สำเร็จ')
    img.src = template.src
  }, [template.src])

  // QR (only when template has it AND we have a link)
  useEffect(() => {
    if (!template.hasQR || !registrationLink) { setQrImg(null); return }
    QRCode.toDataURL(registrationLink, {
      width: slots.qr?.size || 300,
      margin: 0,
      color: { dark: '#000000', light: '#ffffff' },
      errorCorrectionLevel: 'M',
    }).then((url) => {
      const img = new Image()
      img.onload  = () => setQrImg(img)
      img.onerror = () => setLoadErr('QR rendering failed')
      img.src = url
    }).catch((e) => setLoadErr(e?.message || 'QR generation failed'))
  }, [template.hasQR, registrationLink, slots.qr?.size])

  // Company logo (optional)
  useEffect(() => {
    if (!companyId) return
    let cancelled = false
    api.companies.logo.get(companyId).then((r) => {
      if (cancelled || !r?.url) return
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload  = () => { if (!cancelled) setLogoImg(img) }
      img.onerror = () => { /* skip silently */ }
      img.src = r.url
    }).catch(() => {})
    return () => { cancelled = true }
  }, [companyId])

  // Persist layout
  useEffect(() => {
    try { localStorage.setItem(lsKey, JSON.stringify(slots)) } catch {}
  }, [slots, lsKey])

  // Redraw on any input change
  useEffect(() => {
    if (!base || zoom == null || !canvasRef.current) return
    drawFlyer(canvasRef.current, base, qrImg, logoImg, slots, { company, dateText, template })
  }, [base, qrImg, logoImg, slots, company, dateText, zoom, template])

  // Re-position overlays on text/zoom change
  useEffect(() => { bump() }, [company, dateText, slots, zoom])
  useEffect(() => {
    const obs = new ResizeObserver(bump)
    if (wrapRef.current) obs.observe(wrapRef.current)
    return () => obs.disconnect()
  }, [base])

  // First-paint fit zoom
  useEffect(() => {
    if (!base || !stageRef.current) return
    if (zoom != null) return
    setZoom(fitZoom(stageRef.current, template))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base])

  function fitZoom(stage, t) {
    const padding = 32
    const availW = Math.max(120, stage.clientWidth  - padding)
    const availH = Math.max(120, stage.clientHeight - padding)
    return Math.min(availW / t.width, availH / t.height, 1)
  }
  function zoomBy(factor) { setZoom((z) => clamp((z || 0.4) * factor, ZOOM_MIN, ZOOM_MAX)) }
  function zoomFit() { if (stageRef.current) setZoom(fitZoom(stageRef.current, template)) }

  // Save / download
  async function save() {
    if (saving) return
    setSaveErr(null); setSaving(true)
    try {
      const dataUri = canvasRef.current.toDataURL('image/jpeg', 0.85)
      await saveImage(dataUri)
      onSaved?.()
    } catch (e) {
      setSaveErr(extractErr(e))
    } finally {
      setSaving(false)
    }
  }
  function downloadDirect() {
    if (!canvasRef.current) return
    canvasRef.current.toBlob((blob) => {
      if (!blob) return
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `tooth-taxi-${template.key}-${(company || 'flyer').replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '-').slice(0, 60)}.png`
      a.click()
    }, 'image/png')
  }
  async function resetLayout() {
    // Prefer the admin-managed server defaults when present; fall back to
    // the in-code template.defaults otherwise.
    const source = serverDefaults?.slots ? serverDefaults.slots : template.defaults
    const ok = await confirm({
      title: 'คืนค่าเริ่มต้น',
      message: serverDefaults?.slots
        ? 'การเปลี่ยนแปลงล่าสุดของคุณจะหายไป — ใช้ค่าเริ่มต้นที่ผู้ดูแลตั้งไว้แทน'
        : 'การเปลี่ยนแปลงล่าสุดของคุณจะหายไป — ใช้ค่าเริ่มต้นในตัวระบบแทน',
      confirmLabel: 'คืนค่า',
      tone: 'warn',
    })
    if (!ok) return
    setSlots({ ...structuredClone(template.defaults), ...structuredClone(source) })
  }

  // ── Drag / resize ──
  function dragMove(slotKey, e) {
    e.preventDefault(); e.stopPropagation()
    const target = e.currentTarget
    target.setPointerCapture(e.pointerId)
    const scale = scaleFromCanvas(canvasRef.current)
    const start = { x: e.clientX, y: e.clientY }
    const orig  = { ...slots[slotKey] }
    function onMove(ev) {
      const dx = (ev.clientX - start.x) / scale
      const dy = (ev.clientY - start.y) / scale
      setSlots((s) => ({ ...s, [slotKey]: { ...s[slotKey], x: Math.round(orig.x + dx), y: Math.round(orig.y + dy) } }))
    }
    function onUp(ev) {
      target.releasePointerCapture(ev.pointerId)
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
    }
    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp)
  }

  /** kind: 'text' (corner = font size) | 'qr' (corner = square side) | 'logo' (corner = W × H independent) */
  function dragResize(slotKey, kind, e) {
    e.preventDefault(); e.stopPropagation()
    const target = e.currentTarget
    target.setPointerCapture(e.pointerId)
    const scale = scaleFromCanvas(canvasRef.current)
    const start = { x: e.clientX, y: e.clientY }
    const orig  = { ...slots[slotKey] }
    function onMove(ev) {
      const dx = (ev.clientX - start.x) / scale
      const dy = (ev.clientY - start.y) / scale
      setSlots((s) => {
        const next = { ...s[slotKey] }
        if (kind === 'text') {
          next.fontPx = Math.max(14, Math.round(orig.fontPx + dy))
        } else if (kind === 'logo') {
          // Hold Shift for proportional resize; otherwise W and H scale freely.
          if (ev.shiftKey) {
            const aspect = (orig.h || orig.w || 1) / (orig.w || 1)
            const newW = Math.max(40, Math.round(orig.w + Math.max(dx, dy)))
            next.w = newW
            next.h = Math.max(40, Math.round(newW * aspect))
          } else {
            next.w = Math.max(40, Math.round(orig.w + dx))
            next.h = Math.max(40, Math.round(orig.h + dy))
          }
        } else {
          // QR square
          const delta = Math.max(dx, dy)
          next.size = Math.max(80, Math.round(orig.size + delta))
        }
        return { ...s, [slotKey]: next }
      })
    }
    function onUp(ev) {
      target.releasePointerCapture(ev.pointerId)
      target.removeEventListener('pointermove', onMove)
      target.removeEventListener('pointerup', onUp)
    }
    target.addEventListener('pointermove', onMove)
    target.addEventListener('pointerup', onUp)
  }

  // Build overlay rect list — slots are optional so a QR-only template
  // (e.g. CST satisfaction poster) can skip the company/date/logo fields.
  const scale = scaleFromCanvas(canvasRef.current)
  const overlays = [
    ...(slots.company ? [{
      key: 'company', kind: 'text',
      x: slots.company.x, y: slots.company.y,
      w: measureText(company, slots.company.fontPx) || 200,
      h: slots.company.fontPx, label: 'Company',
    }] : []),
    ...(slots.date ? [{
      key: 'date', kind: 'text',
      x: slots.date.x, y: slots.date.y,
      w: measureText((template.dateLabel || '') + dateText, slots.date.fontPx) || 200,
      h: slots.date.fontPx, label: 'Date',
    }] : []),
    ...(template.hasQR && slots.qr ? [{
      key: 'qr', kind: 'qr',
      x: slots.qr.x, y: slots.qr.y, w: slots.qr.size, h: slots.qr.size, label: 'QR',
    }] : []),
    ...(logoImg && slots.logo ? [{
      key: 'logo', kind: 'logo',
      x: slots.logo.x, y: slots.logo.y,
      w: slots.logo.w ?? slots.logo.size ?? 200,
      h: slots.logo.h ?? slots.logo.size ?? 200,
      label: 'Logo',
    }] : []),
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-4">
      <div className="absolute inset-0 bg-zinc-900/55 backdrop-blur-[2px]" onClick={() => !saving && onClose()} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[1180px] h-[94vh] flex flex-col">
        <header className="px-5 py-3.5 border-b border-zinc-100 flex items-center justify-between shrink-0">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-amber-700/80 font-semibold">{template.label}</div>
            <h2 className="text-[16px] font-bold text-zinc-900">สร้างภาพประชาสัมพันธ์</h2>
          </div>
          <button onClick={onClose} disabled={saving} className="w-8 h-8 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 inline-flex items-center justify-center disabled:opacity-50">
            <X size={16} />
          </button>
        </header>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[340px_1fr] min-h-0">
          <aside className="p-5 md:p-6 space-y-4 border-b md:border-b-0 md:border-r border-zinc-100 overflow-y-auto bg-zinc-50/40">
            {template.defaults.company && (
              <Field icon={Building2} label="ชื่อบริษัท">
                <input value={company} maxLength={120} onChange={(e) => setCompany(e.target.value)} className={input} />
              </Field>
            )}
            {template.defaults.date && (
              <Field icon={CalendarRange} label="วันที่จัดบริการ" hint={template.dateLabel ? `แสดงเป็น "${template.dateLabel.trim()} …"` : undefined}>
                <input value={dateText} maxLength={120} onChange={(e) => setDateText(e.target.value)} className={input} />
              </Field>
            )}

            <div className="pt-3 border-t border-zinc-200/80">
              <p className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-1.5">วิธีปรับ layout</p>
              <ul className="text-[12px] text-zinc-600 space-y-1 leading-relaxed">
                <li>• <strong>ลาก</strong> กล่องสีส้มเพื่อ <strong>ย้าย</strong></li>
                <li>• ลาก <strong>มุมล่างขวา</strong> เพื่อปรับ <strong>ขนาดตัวอักษร</strong> (ข้อความ)</li>
                {template.hasQR && (<li>• ลาก <strong>มุมล่างขวา</strong> เพื่อปรับ <strong>ขนาด QR</strong> (square)</li>)}
                <li>• สำหรับโลโก้ — มุมล่างขวาปรับ <strong>กว้าง × สูง</strong> อิสระ · กด <kbd className="px-1 ring-1 ring-zinc-300 rounded text-[10px]">⇧ Shift</kbd> ขณะลากเพื่อรักษาอัตราส่วน</li>
                <li>• ตำแหน่งจะถูกจำไว้ใน browser นี้</li>
              </ul>
            </div>

            <button onClick={resetLayout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] text-zinc-600 hover:bg-zinc-100 ring-1 ring-zinc-200">
              <RotateCcw size={12} /> คืนค่าเริ่มต้น
            </button>

            {/* Admin-only: persist the current layout as the new server-side default.
                Visible only when the logged-in employee's Xano roles include "Admin". */}
            {admin && (
              <div className="pt-3 border-t border-zinc-200/80">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-amber-700 mb-1.5 inline-flex items-center gap-1">
                  <Shield size={11} /> Admin
                </p>
                <button onClick={saveAsDefault} disabled={savingDefault}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg ring-1 ring-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[12.5px] font-medium disabled:opacity-60">
                  {savingDefault
                    ? <><Loader2 size={12} className="animate-spin" /> กำลังบันทึก…</>
                    : savedDefault
                      ? <><Check size={12} className="text-emerald-600" /> บันทึกเป็นค่าเริ่มต้นแล้ว</>
                      : <><Save size={12} /> บันทึกตำแหน่งปัจจุบันเป็นค่าเริ่มต้น</>}
                </button>
                {serverDefaults?.updated_at && (
                  <p className="mt-1.5 text-[10.5px] text-zinc-500">
                    ล่าสุดอัปเดต: {new Date(serverDefaults.updated_at * 1000).toLocaleString('th-TH-u-ca-gregory')}
                    {serverDefaults.updated_by_code ? ` · โดย ${serverDefaults.updated_by_code}` : ''}
                  </p>
                )}
              </div>
            )}

            {saveErr && (
              <div className="px-3 py-2 rounded-lg bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12.5px] flex items-center gap-2">
                <AlertTriangle size={13} /> {saveErr}
              </div>
            )}
          </aside>

          <section ref={stageRef} className="bg-gradient-to-br from-amber-50/40 to-orange-50/20 overflow-auto relative">
            {loadErr ? (
              <div className="px-3 py-2 m-4 rounded bg-rose-50 ring-1 ring-rose-200 text-rose-700 text-[12.5px] inline-flex items-center gap-1.5">
                <AlertTriangle size={13} /> {loadErr}
              </div>
            ) : (!base || zoom == null || (template.hasQR && !qrImg)) ? (
              <div className="text-zinc-400 inline-flex items-center gap-2 text-[13px] py-12 w-full justify-center">
                <Loader2 size={14} className="animate-spin" /> กำลังเตรียมภาพ…
              </div>
            ) : (
              <>
                <div className="sticky top-3 z-20 ml-3 inline-flex items-center gap-1 px-1.5 py-1 rounded-lg bg-white shadow-md ring-1 ring-zinc-200 select-none w-fit">
                  <ZoomBtn onClick={() => zoomBy(1 / ZOOM_STEP)} disabled={zoom <= ZOOM_MIN + 0.001}><ZoomOut size={13} /></ZoomBtn>
                  <span className="px-2 text-[11.5px] font-semibold tabular-nums text-zinc-700 min-w-[44px] text-center">{Math.round(zoom * 100)}%</span>
                  <ZoomBtn onClick={() => zoomBy(ZOOM_STEP)} disabled={zoom >= ZOOM_MAX - 0.001}><ZoomIn size={13} /></ZoomBtn>
                  <span className="w-px h-4 bg-zinc-200 mx-1" />
                  <ZoomBtn onClick={zoomFit} title="Fit"><Maximize2 size={13} /></ZoomBtn>
                </div>

                <div className="p-4 md:p-6 flex items-start justify-center">
                  <div ref={wrapRef} className="relative inline-block"
                    style={{ width: template.width * zoom, height: template.height * zoom }}>
                    <canvas
                      ref={canvasRef}
                      width={template.width}
                      height={template.height}
                      style={{ width: template.width * zoom, height: template.height * zoom }}
                      className="rounded-xl ring-1 ring-zinc-200 shadow-xl bg-white block"
                    />
                    {overlays.map((o) => (
                      <Overlay
                        key={o.key}
                        rect={{ x: o.x * scale, y: o.y * scale, w: o.w * scale, h: o.h * scale }}
                        label={o.label}
                        onDragMove={(e) => dragMove(o.key, e)}
                        onDragResize={(e) => dragResize(o.key, o.kind, e)}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>

        <footer className="px-5 py-3 border-t border-zinc-100 flex items-center justify-between gap-3 shrink-0">
          <button onClick={downloadDirect} disabled={!base}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[13px] text-zinc-700 hover:bg-zinc-100 ring-1 ring-zinc-200 disabled:opacity-50">
            <Download size={13} /> ดาวน์โหลดเลย (PNG)
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={saving} className="px-3 py-1.5 rounded-md text-[13px] text-zinc-600 hover:bg-zinc-100">ยกเลิก</button>
            <button onClick={save} disabled={saving || !base}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-medium disabled:opacity-60">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} บันทึกภาพ
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}


/* ─────────────────────────── overlay + zoom button ─────────────────────── */

function ZoomBtn({ children, onClick, disabled, title }) {
  return (
    <button onClick={onClick} disabled={disabled} title={title}
      className="w-7 h-7 rounded-md inline-flex items-center justify-center text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-zinc-600">
      {children}
    </button>
  )
}

function clamp(v, min, max) { return Math.max(min, Math.min(max, v)) }

function Overlay({ rect, label, onDragMove, onDragResize }) {
  const [dragging, setDragging] = useState(false)
  return (
    <div
      onPointerDown={(e) => { setDragging(true); onDragMove(e) }}
      onPointerUp={() => setDragging(false)}
      className="absolute group select-none touch-none"
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, cursor: dragging ? 'grabbing' : 'grab' }}
    >
      <div className={'absolute inset-0 rounded transition-colors ring-2 ring-dashed ' +
        (dragging ? 'ring-amber-500/90 bg-amber-100/10' : 'ring-amber-400/0 group-hover:ring-amber-500/70')} />
      <div className={'absolute -top-5 left-0 px-1.5 py-0.5 rounded bg-amber-500 text-white text-[10px] font-bold tracking-wider uppercase ' +
        (dragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')}>
        {label}
      </div>
      <div
        onPointerDown={(e) => { e.stopPropagation(); onDragResize(e) }}
        className="absolute -right-2 -bottom-2 w-4 h-4 rounded-sm bg-amber-500 ring-2 ring-white shadow-md"
        style={{ cursor: 'nwse-resize' }}
      />
    </div>
  )
}


/* ─────────────────────────── canvas composer ─────────────────────────── */

function drawFlyer(canvas, base, qrImg, logoImg, slots, { company, dateText, template }) {
  const ctx = canvas.getContext('2d')
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(base, 0, 0)

  // Logo (drawn first so text/QR overlay on top if positioned overlapping).
  if (logoImg && slots.logo) {
    const L = slots.logo
    const w = L.w ?? L.size ?? 200
    const h = L.h ?? L.size ?? 200
    drawImageContain(ctx, logoImg, L.x, L.y, w, h)
  }

  // QR
  if (template.hasQR && qrImg && slots.qr) {
    const Q = slots.qr
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(Q.x, Q.y, Q.size, Q.size)
    ctx.drawImage(qrImg, Q.x, Q.y, Q.size, Q.size)
  }

  // Text — both slots optional so a QR-only template can omit them entirely.
  ctx.fillStyle = TEXT_COLOR
  ctx.textBaseline = 'top'

  if (slots.company) {
    const C = slots.company
    ctx.font = `700 ${C.fontPx}px ${FONT_STACK}`
    ctx.fillText(company || '', C.x, C.y)
  }
  if (slots.date) {
    const D = slots.date
    ctx.font = `700 ${D.fontPx}px ${FONT_STACK}`
    ctx.fillText((template.dateLabel || '') + (dateText || ''), D.x, D.y)
  }
}

/** Draw an image into a target rect with object-fit: contain semantics —
 *  the image keeps its aspect ratio and is letterboxed (transparent padding)
 *  within the box. Non-square logos display correctly. */
function drawImageContain(ctx, img, dx, dy, dw, dh) {
  const ir = img.width / img.height
  const dr = dw / dh
  let renderW, renderH
  if (ir > dr) { renderW = dw; renderH = dw / ir }
  else         { renderH = dh; renderW = dh * ir }
  const offsetX = dx + (dw - renderW) / 2
  const offsetY = dy + (dh - renderH) / 2
  ctx.drawImage(img, offsetX, offsetY, renderW, renderH)
}


/* ─────────────────────────── helpers ─────────────────────────── */

function Field({ icon: Icon, label, hint, children }) {
  return (
    <label className="block">
      <span className="flex items-center justify-between mb-1.5 px-0.5">
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-zinc-700">
          {Icon && <Icon size={13} className="text-amber-500" />} {label}
        </span>
        {hint && <span className="text-[11px] text-zinc-400">{hint}</span>}
      </span>
      {children}
    </label>
  )
}
const input = 'w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg text-[13.5px] focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-100'

function scaleFromCanvas(canvas) {
  if (!canvas || !canvas.clientWidth) return 1
  return canvas.clientWidth / canvas.width
}

let _measureCtx = null
function measureText(text, fontPx) {
  if (!_measureCtx) {
    const c = document.createElement('canvas')
    _measureCtx = c.getContext('2d')
  }
  _measureCtx.font = `700 ${fontPx}px ${FONT_STACK}`
  return _measureCtx.measureText(text || '').width
}

function defaultDateText(startKey, endKey) {
  if (!startKey) return ''
  const sStr = fmtKey(startKey)
  if (!endKey || endKey === startKey) return sStr
  return `${sStr} – ${fmtKey(endKey)}`
}
function fmtKey(key) {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString('th-TH-u-ca-gregory', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function extractErr(e) {
  const m = (e?.message || '').match(/:\s*\{?"?error"?"?:\s*"?([^"}\n]+)/)
  return m ? m[1] : (e?.message || 'error')
}
